import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Plus, Settings } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { AdditionalOption } from './types';
import { toast } from 'sonner';
import { AdditionalOptionHeader } from './components/AdditionalOptionHeader';
import { DropdownChoiceManager } from './components/DropdownChoiceManager';
import { OptionPricingDisplay } from './components/OptionPricingDisplay';

interface AdditionalOptionsCardProps {
  additionalOptions: AdditionalOption[];
  setAdditionalOptions: (options: AdditionalOption[]) => void;
  currency: string;
  onAddPricing: (optionId: string, isCore: boolean, optionType: string, choiceIndex?: number) => void;
  onRemovePricing: (optionId: string, isCore: boolean, pricingIndex: number) => void;
  renderPricingPreview: (pricing: any) => string | null;
  onShowAddOption: () => void;
}

export function AdditionalOptionsCard({
  additionalOptions,
  setAdditionalOptions,
  currency,
  onAddPricing,
  onRemovePricing,
  renderPricingPreview,
  onShowAddOption
}: AdditionalOptionsCardProps) {
  const currencySymbol = currency === 'EUR' ? '€' : currency === 'GBP' ? '£' : '$';

  const removeAdditionalOption = (id: string) => {
    setAdditionalOptions(additionalOptions.filter(opt => opt.id !== id));
    toast.success('Option removed');
  };

  const updateAdditionalOption = (id: string, updates: Partial<AdditionalOption>) => {
    setAdditionalOptions(additionalOptions.map(opt => 
      opt.id === id ? { ...opt, ...updates } : opt
    ));
  };

  // Simplified render function - complexity moved to child components

  return (
    <Card className="border border-slate-200 shadow-md bg-white">
      <CardHeader className="pb-4 border-b border-slate-100">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-purple-100 border border-purple-200 flex items-center justify-center">
              <Plus className="w-4 h-4 text-purple-600" />
            </div>
            Additional Options
            <Badge variant="outline" className="border-slate-200">
              Optional
            </Badge>
          </CardTitle>
          <Button
            onClick={onShowAddOption}
            className="bg-gradient-to-r from-action-500 to-action-600 hover:from-action-600 hover:to-action-700 shadow-md"
            size="sm"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Option
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-6">
        <AnimatePresence>
          {additionalOptions.length === 0 ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                <Plus className="w-8 h-8 text-slate-400" />
              </div>
              <p className="text-slate-600 mb-4">No additional options yet</p>
              <p className="text-sm text-slate-500">
                Add extras like meals, equipment, or activity levels
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {additionalOptions.map((option) => (
                <motion.div
                  key={option.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="p-4 border border-slate-200 rounded-lg bg-white"
                >
                  <AdditionalOptionHeader
                    option={option}
                    index={0}
                    onUpdateOption={updateAdditionalOption}
                    onRemoveOption={removeAdditionalOption}
                  />

                  {/* Choice Management for Dropdown Options */}
                  {option.type === 'select' && (
                    <DropdownChoiceManager
                      option={option}
                      currency={currency}
                      onUpdateOption={updateAdditionalOption}
                      onAddPricing={onAddPricing}
                      onRemovePricing={onRemovePricing}
                      renderPricingPreview={renderPricingPreview}
                    />
                  )}

                  {/* Option-level Pricing for Non-dropdown Options */}
                  {option.type !== 'select' && (
                    <OptionPricingDisplay
                      option={option}
                      onAddPricing={onAddPricing}
                      onRemovePricing={onRemovePricing}
                      renderPricingPreview={renderPricingPreview}
                    />
                  )}
                </motion.div>
              ))}
            </div>
          )}
        </AnimatePresence>
      </CardContent>
    </Card>
  );
} 